/** Modal dialogs: help, recipes, leaderboard, run-submit, victory. */
import {
  GEMS,
  GREATER_SLATES,
  RECIPES,
  SLATE_MIN_DIST,
  SLATE_RECIPES,
  SLATES,
  SPECIALS,
  TIERS,
  TIER_COLORS,
} from '../data/balance.js';
import { byId, clear, el } from './dom.js';

export interface LeaderboardEntry {
  readonly id: string;
  readonly name: string;
  readonly wave: number;
  readonly lives: number;
  readonly outcome: string;
  readonly player: string;
  readonly date?: string;
}

export function showModal(id: string): void {
  byId(id).classList.add('open');
}

export function hideModal(id: string): void {
  byId(id).classList.remove('open');
}

export function hideAllModals(): void {
  document.querySelectorAll('.modal.open').forEach((m) => {
    m.classList.remove('open');
  });
}

/** Wire close buttons + backdrop clicks once at startup. */
export function wireModals(): void {
  document.querySelectorAll<HTMLElement>('.modal').forEach((modal) => {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.remove('open');
    });
    modal.querySelectorAll('.modal-close').forEach((btn) => {
      btn.addEventListener('click', () => {
        modal.classList.remove('open');
      });
    });
  });
}

export function buildRecipeList(): void {
  const box = byId('recipeList');
  clear(box);
  for (const r of RECIPES) {
    const s = SPECIALS[r.key];
    const parts = el('small', {});
    r.comps.forEach(([t, q], idx) => {
      if (idx > 0) parts.append('  +  ');
      parts.append(
        el('span', {
          className: `sw ${GEMS[t].shape}`,
          style: { background: GEMS[t].color },
        }),
        el('span', { text: TIERS[q]!, style: { color: TIER_COLORS[q]! } }),
        ` ${GEMS[t].name}`,
      );
    });
    box.append(
      el('div', { className: 'recipe' }, [
        el('b', { style: { color: s.color } }, [
          el('span', { className: 'sw star', style: { background: s.color } }),
          s.name,
        ]),
        ` · ${s.desc}`,
        el('br'),
        parts,
      ]),
    );
  }

  // ---- slates (endgame layer) ----
  box.append(
    el('h3', { text: 'Slates', style: { marginTop: '14px' } }),
    el('p', {}, [
      el('small', {
        text:
          'Forged from junk rolls, socketed into a rock. One Normal gem of the anchor type ' +
          `+ two Flawed gems of any type. Same slates must sit ${SLATE_MIN_DIST}+ cells apart.`,
      }),
    ]),
  );
  for (const r of SLATE_RECIPES) {
    const s = SLATES[r.key];
    box.append(
      el('div', { className: 'recipe' }, [
        el('b', { style: { color: s.color } }, [
          el('span', { className: `sw diamond`, style: { background: s.color } }),
          s.name,
        ]),
        ` · ${s.desc}`,
        el('br'),
        el('small', {}, [
          el('span', {
            className: `sw ${GEMS[r.anchor].shape}`,
            style: { background: GEMS[r.anchor].color },
          }),
          el('span', { text: TIERS[2], style: { color: TIER_COLORS[2] } }),
          ` ${GEMS[r.anchor].name}  +  2× `,
          el('span', { text: TIERS[1], style: { color: TIER_COLORS[1] } }),
          ' any gem',
        ]),
      ]),
    );
  }
  box.append(
    el('h3', { text: 'Greater slates', style: { marginTop: '14px' } }),
    el('p', {}, [
      el('small', { text: 'Combine two placed base slates (tap one, then its partner).' }),
    ]),
  );
  for (const g of GREATER_SLATES) {
    const s = SLATES[g.key];
    box.append(
      el('div', { className: 'recipe' }, [
        el('b', { style: { color: s.color } }, [
          el('span', { className: `sw diamond`, style: { background: s.color } }),
          s.name,
        ]),
        ` · ${s.desc}`,
        el('br'),
        el('small', {}, [
          el('span', { text: SLATES[g.pair[0]].name, style: { color: SLATES[g.pair[0]].color } }),
          '  +  ',
          el('span', { text: SLATES[g.pair[1]].name, style: { color: SLATES[g.pair[1]].color } }),
        ]),
      ]),
    );
  }
}

/** The wave-50 victory choice: continue into the endless waves or retire. */
export function showWonModal(lives: number): void {
  byId('wonText').textContent =
    `Wave 50 survived — lives restored to ${lives}. ` +
    'From here every wave grows stronger, a mega boss looms every 50th, and your record is ' +
    'sealed when you fall or retire.';
  showModal('wonModal');
}

export function renderLeaderboard(
  entries: readonly LeaderboardEntry[] | null,
  onWatch: (entry: LeaderboardEntry) => void,
): void {
  const box = byId('lbList');
  clear(box);
  if (!entries) {
    box.append(el('div', { className: 'lb-empty', text: 'Leaderboard server not reachable.' }));
    return;
  }
  if (!entries.length) {
    box.append(
      el('div', { className: 'lb-empty' }, [
        'No runs submitted yet.',
        el('br'),
        el('b', { text: 'Finish a game and claim the top spot.' }),
      ]),
    );
    return;
  }
  const head = el('div', { className: 'lb-head' });
  for (const h of ['#', 'Player', 'Wave', 'Lives', 'Date', ''])
    head.append(el('span', { text: h }));
  box.append(head);
  entries.forEach((entry, i) => {
    const name = el('span', { className: 'lb-name', text: entry.name });
    if (entry.outcome === 'victory') name.append(' 🏆');
    if (entry.player === 'agent') {
      name.append(
        el('span', {
          className: 'chip',
          text: 'BOT',
          style: { background: '#7ecbff22', color: '#7ecbff', border: '1px solid #7ecbff55' },
        }),
      );
    }
    const watch = el('button', { text: '▶ Watch' });
    watch.addEventListener('click', () => {
      onWatch(entry);
    });
    box.append(
      el('div', { className: 'lb-row' }, [
        el('span', { className: `lb-rank r${i + 1}`, text: String(i + 1) }),
        name,
        el('span', { className: 'lb-wave', text: String(entry.wave) }),
        el('span', { className: 'lb-lives', text: `♥ ${entry.lives}` }),
        el('span', { className: 'lb-date', text: (entry.date ?? '').slice(0, 10) }),
        watch,
      ]),
    );
  });
}

export function showSubmitModal(text: string, savedName: string): void {
  byId('submitText').textContent = text;
  byId('nameInput', HTMLInputElement).value = savedName;
  showModal('submitModal');
}
